// BUILD-049: the compact "HR tasks" section of My Work - checklist tasks (onboarding AND offboarding) HR has named the signed-in Employee to do.
//
// It shows the HR row itself (nothing is copied into My Work) and can start it, add a task note and mark ordinary work Done. It never offers edit, reassign, reopen,
// "not needed" or history, and a task tied to a document has no Done (the HR document review completes it). It fails on its own and says so; the lists below are untouched.
import { useState } from 'react';
import { useHrAssignedTasks } from './useHrAssignedTasks';
import {
  contextLine, describeHrSection, dueText, DOCUMENT_LOCK_TEXT, isOverdue, kindLabel, NOTE_VISIBILITY_LABEL, phoenixToday, statusLabel, taskOffers,
} from './hrAssignedTasksModel';
import type { HrTaskRow, HrTasksState } from './hrAssignedTasksModel';
import type { HrTasksController } from './hrAssignedTasksController';

const STATUS_TONE: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-700', in_progress: 'bg-amber-50 text-amber-800', completed: 'bg-green-50 text-green-800', exception: 'bg-gray-100 text-gray-500',
};
const KIND_TONE: Record<string, string> = { onboarding: 'bg-sky-50 text-sky-800 border-sky-200', offboarding: 'bg-violet-50 text-violet-800 border-violet-200' };
const SMALL_BUTTON = 'rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-60';
const DONE_BUTTON = 'rounded bg-[#1f6a99] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#185a84] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-60';

function TaskRow({ row, state, controller, today }: { row: HrTaskRow; state: HrTasksState; controller: HrTasksController; today: string }) {
  const offers = taskOffers(row);
  const busy = state.busy[row.assignmentTaskId] === true;
  const rowError = state.rowError[row.assignmentTaskId];
  const [noteOpen, setNoteOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const overdue = isOverdue(row, today);
  const openNote = (): void => { setDraft(row.taskNote ?? ''); setNoteOpen(true); };
  const saveNote = async (): Promise<void> => {
    const ok = await controller.act(row.assignmentTaskId, { note: draft.trim() === '' ? null : draft });
    if (ok) setNoteOpen(false);
  };
  return (
    <li className="px-3 py-2.5" data-testid="hr-task-row" data-task-id={row.assignmentTaskId}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${KIND_TONE[row.checklistKind] ?? ''}`} data-testid="hr-task-kind">{kindLabel(row.checklistKind)}</span>
            <span className="text-sm font-medium text-gray-900" data-testid="hr-task-title">{row.title}</span>
            <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[row.status] ?? ''}`} data-testid="hr-task-status">{statusLabel(row.status)}</span>
            {overdue && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700" data-testid="hr-task-overdue">Overdue</span>}
          </div>
          <div className="mt-0.5 text-xs text-gray-600" data-testid="hr-task-context">
            {contextLine(row)} {'\u00b7'} Due {dueText(row.dueDate)}{row.assignedBy.displayName ? ` \u00b7 Assigned by ${row.assignedBy.displayName}` : ''} {'\u00b7'} From HR
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {offers.start && <button type="button" className={SMALL_BUTTON} disabled={busy} onClick={() => void controller.act(row.assignmentTaskId, { status: 'in_progress' })} data-testid="hr-task-start">Start</button>}
          {offers.note && <button type="button" className={SMALL_BUTTON} disabled={busy} onClick={openNote} data-testid="hr-task-note-open">{row.taskNote ? 'Edit note' : 'Add note'}</button>}
          {offers.done && <button type="button" className={DONE_BUTTON} disabled={busy} onClick={() => void controller.act(row.assignmentTaskId, { status: 'completed' })} data-testid="hr-task-done">Done</button>}
        </div>
      </div>
      {row.taskNote && !noteOpen && (
        <p className="mt-1.5 whitespace-pre-wrap rounded bg-gray-50 px-2 py-1 text-xs text-gray-800" data-testid="hr-task-note"><span className="font-medium text-gray-600">Task note: </span>{row.taskNote}</p>
      )}
      {noteOpen && (
        <div className="mt-1.5" data-testid="hr-task-note-form">
          <label className="block text-[11px] font-medium text-gray-600" htmlFor={`hr-note-${row.assignmentTaskId}`}>{NOTE_VISIBILITY_LABEL}</label>
          <textarea id={`hr-note-${row.assignmentTaskId}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={2000} className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm" />
          <div className="mt-1 flex gap-1.5">
            <button type="button" className={DONE_BUTTON} disabled={busy} onClick={() => void saveNote()} data-testid="hr-task-note-save">Save note</button>
            <button type="button" className={SMALL_BUTTON} onClick={() => setNoteOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      {offers.documentLock && <p className="mt-1 text-xs text-gray-600" data-testid="hr-task-document-lock">{DOCUMENT_LOCK_TEXT}</p>}
      {rowError && (
        <p role="alert" className="mt-1 rounded bg-red-50 px-2 py-1 text-xs text-red-700" data-testid="hr-task-row-error">
          {rowError}<button type="button" onClick={() => controller.dismissRowError(row.assignmentTaskId)} aria-label="Dismiss message" className="float-right font-bold">&times;</button>
        </p>
      )}
    </li>
  );
}

export function HrAssignedTasksSection() {
  const { state, controller } = useHrAssignedTasks();
  const view = describeHrSection(state);
  if (view.kind === 'hidden' || view.kind === 'loading') return null;
  if (view.kind === 'error') {
    return (
      <section className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="hr-tasks-unavailable" aria-label="HR tasks">
        {view.message}
        <button type="button" className="ml-2 font-medium underline" onClick={() => void controller.load()} data-testid="hr-tasks-retry">Retry</button>
      </section>
    );
  }
  const today = phoenixToday(new Date());
  return (
    <section className="mb-3 rounded-lg border border-gray-200 bg-white" aria-label="HR tasks" data-testid="hr-tasks-section">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <h2 className="text-sm font-semibold text-gray-900">HR tasks <span className="font-normal text-gray-500" data-testid="hr-tasks-count">{view.view === 'open' ? `${view.total} open` : `${view.total} done`}</span></h2>
        <div className="flex items-center gap-1" role="group" aria-label="HR tasks view">
          {(['open', 'done'] as const).map((v) => (
            <button key={v} type="button" onClick={() => void controller.setView(v)} aria-pressed={view.view === v} data-testid={`hr-tasks-view-${v}`}
              className={`rounded px-2 py-0.5 text-xs font-medium ${view.view === v ? 'bg-[#1f6a99] text-white' : 'text-[#1f6a99] hover:underline'}`}>{v === 'open' ? 'Open' : 'Done'}</button>
          ))}
        </div>
      </header>
      {view.refreshNote && <p role="status" className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="hr-tasks-note">{view.refreshNote}<button type="button" className="ml-2 font-medium underline" onClick={() => void controller.refresh()}>Retry</button></p>}
      {view.rows.length === 0 ? (
        <p className="px-3 py-3 text-sm text-gray-600" data-testid="hr-tasks-empty">{view.view === 'open' ? 'No open HR tasks.' : 'No finished HR tasks yet.'}</p>
      ) : (
        <ul className="divide-y divide-gray-100">{view.rows.map((row) => <TaskRow key={row.assignmentTaskId} row={row} state={state} controller={controller} today={today} />)}</ul>
      )}
      <footer className="flex items-center justify-between px-3 py-1.5 text-xs text-gray-500">
        <span data-testid="hr-tasks-showing">Showing {view.showing} of {view.total}</span>
        {view.hasMore && <button type="button" className="font-medium text-[#1f6a99] hover:underline" onClick={() => void controller.loadMore()} data-testid="hr-tasks-more">Show more</button>}
      </footer>
    </section>
  );
}
